import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { adminCompanyApiService } from "infrastructure/api/admin/admin-company-api-service";
import type {
  ActionUrgency,
  CollectorStatus,
  CompanyWithStatus,
  KpiDailyReportStatus,
  ProgramCode,
} from "domain/types/admin-company";

// ───────────────────────────────────────────────────────────────────────────
// helpers
// ───────────────────────────────────────────────────────────────────────────

const URGENCY_BAR: Record<ActionUrgency, string> = {
  URGENT: "#F24A4A",
  HIGH: "#E0A800",
  MEDIUM: "#C9A227",
};

const COLLECTOR_LABEL: Record<CollectorStatus, string> = {
  NORMAL: "정상",
  WARNING: "경고",
  OFFLINE: "오프라인",
  NOT_INSTALLED: "미설치",
};

const COLLECTOR_DOT: Record<CollectorStatus, string> = {
  NORMAL: "#27AE60",
  WARNING: "#E0A800",
  OFFLINE: "#F24A4A",
  NOT_INSTALLED: "#888888",
};

const KPI_REPORT_LABEL: Record<KpiDailyReportStatus, string> = {
  NORMAL: "정상",
  NOT_REPORTED: "미보고",
  ERROR: "오류",
  // PENDING = WORKSHOP 미가입 또는 KPI 인증키/카테고리 미설정. KPI 보고 자체가 의미 없는 회사라
  // "승인 대기" 같은 표현 대신 무의미 dash 만 노출.
  PENDING: "—",
};

const KPI_REPORT_DOT: Record<KpiDailyReportStatus, string> = {
  NORMAL: "#27AE60",
  NOT_REPORTED: "#888888",
  ERROR: "#F24A4A",
  PENDING: "#888888",
};

// 화면 분류 필터는 단일 타입 선택이므로, 두 타입을 모두 등록한 회사는 양쪽 모두에서 노출된다.
const CLASSIFICATION_LABEL: Record<ProgramCode, string> = {
  SMART_WORKSHOP: "스마트 공방",
  SMART_FACTORY: "스마트 공장",
};

/** 회사명 → 2글자 약어 로고 문자열. (백엔드에는 로고 코드가 없으므로 클라이언트에서 생성) */
const buildLogoCode = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return "??";
  // 영문/숫자는 앞 2글자, 그 외(한글 등)는 첫 글자 1개
  if (/^[A-Za-z0-9]/.test(trimmed)) {
    return trimmed.slice(0, 2).toUpperCase();
  }
  return trimmed.charAt(0);
};

/** "yyyy-MM-ddTHH:mm:ss[...]" → "yyyy-MM-dd HH:mm" (날짜 정보 없으면 "—"). */
const formatLastReportedAt = (value?: string | null): string => {
  if (!value) return "—";
  // LocalDateTime 직렬화 형태(콜론 포함 ISO) 가정. 'T' → 공백 + 초/밀리초 제거.
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) return `${match[1]} ${match[2]}`;
  return value;
};

const formatCollectorSummary = (row: CompanyWithStatus): React.ReactNode => {
  const { collectorStatus: status, collectorStats: stats } = row;

  if (stats && status === "NORMAL") {
    if (stats.warning > 0 || stats.offline > 0) {
      return (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: COLLECTOR_DOT.NORMAL }}
            />
            <span>정상 {stats.normal}</span>
          </span>
          {stats.warning > 0 && (
            <>
              <span className="text-[#D9D9D9]">/</span>
              <span className="flex items-center gap-1">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: COLLECTOR_DOT.WARNING }}
                />
                <span>경고 {stats.warning}</span>
              </span>
            </>
          )}
          {stats.offline > 0 && (
            <>
              <span className="text-[#D9D9D9]">/</span>
              <span className="flex items-center gap-1">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: COLLECTOR_DOT.OFFLINE }}
                />
                <span>오프라인 {stats.offline}</span>
              </span>
            </>
          )}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{ background: COLLECTOR_DOT.NORMAL }}
        />
        <span>정상 {stats.normal}</span>
      </div>
    );
  }

  if (stats && stats.warning + stats.offline > 0) {
    return (
      <div className="flex items-center gap-2">
        {stats.normal > 0 && (
          <>
            <span className="flex items-center gap-1">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: COLLECTOR_DOT.NORMAL }}
              />
              <span>정상 {stats.normal}</span>
            </span>
            <span className="text-[#D9D9D9]">/</span>
          </>
        )}
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: COLLECTOR_DOT[status] }}
          />
          <span>
            {COLLECTOR_LABEL[status]}{" "}
            {stats[status === "WARNING" ? "warning" : "offline"]}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{ background: COLLECTOR_DOT[status] }}
      />
      <span>{COLLECTOR_LABEL[status]}</span>
    </div>
  );
};

/**
 * 회사의 분류 표기. 복수 카테고리 등록 시 슬래시로 묶어 표기.
 * ACHIEVEMENT_NOTICE(지원사업 고지)는 업체 분류가 아니므로 제외 — 회사명 옆 NoticeBadge 로 표기.
 */
const formatClassification = (row: CompanyWithStatus): string => {
  const labels = (row.programCodes ?? [])
    .filter((code): code is ProgramCode => code in CLASSIFICATION_LABEL)
    .map((code) => CLASSIFICATION_LABEL[code]);
  return labels.length > 0 ? labels.join(" / ") : "—";
};

/** 지원사업 고지(ACHIEVEMENT_NOTICE) 등록 회사 — 회사명 옆에 붙는 미니 뱃지. */
const NoticeBadge: React.FC<{ row: CompanyWithStatus }> = ({ row }) =>
  (row.programCodes ?? []).includes("ACHIEVEMENT_NOTICE") ? (
    <span className="text-[11px] font-medium rounded px-1.5 py-0.5 bg-[#E8EFFF] text-[#4285F4] whitespace-nowrap flex-shrink-0">
      Notice
    </span>
  ) : null;

// ───────────────────────────────────────────────────────────────────────────
// shared sub-components
// ───────────────────────────────────────────────────────────────────────────

const LogoMini: React.FC<{ code: string }> = ({ code }) => (
  <span
    className="rounded-md inline-flex items-center justify-center text-[10px] font-bold flex-shrink-0"
    style={{ width: 30, height: 30, background: "#F0F1F4", color: "#555" }}
  >
    {code}
  </span>
);

const KpiReportCell: React.FC<{
  status: KpiDailyReportStatus;
  note?: string | null;
}> = ({ status, note }) => {
  // KPI 보고 자체가 의미 없는 회사(WORKSHOP 미가입·미설정) → dot 없이 "—" 만.
  if (status === "PENDING") {
    return <span className="text-[#888]">{note ?? "—"}</span>;
  }
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{ background: KPI_REPORT_DOT[status] }}
      />
      <span className={status === "NORMAL" ? "" : "text-[#555]"}>
        {note ?? KPI_REPORT_LABEL[status]}
      </span>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// main page
// ───────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const ManageRegisteredCompanies1: React.FC = () => {
  const navigate = useNavigate();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [classificationFilter, setClassificationFilter] = useState<
    "" | ProgramCode
  >("");
  const [collectorFilter, setCollectorFilter] = useState<"" | CollectorStatus>(
    "",
  );
  const [kpiReportFilter, setKpiReportFilter] = useState<
    "" | KpiDailyReportStatus
  >("");

  const [companies, setCompanies] = useState<CompanyWithStatus[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [actionRequired, setActionRequired] = useState<CompanyWithStatus[]>([]);
  const [page, setPage] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasMore = companies.length < totalElements;

  // 페이지 변경 시 회사 목록 로드. page 0 = reset, page 1+ = append (무한 스크롤). actionRequired 는 초기 1회만.
  useEffect(() => {
    let cancelled = false;

    const fetchPage = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const [listRes, actionRes] = await Promise.all([
          adminCompanyApiService.listCompaniesWithStatus({
            page,
            size: PAGE_SIZE,
          }),
          page === 0
            ? adminCompanyApiService.listActionRequiredCompanies()
            : Promise.resolve(null),
        ]);

        if (cancelled) return;

        const data = listRes.data;
        const items = data?.items ?? [];
        setCompanies((prev) => (page === 0 ? items : [...prev, ...items]));
        setTotalElements(data?.totalElements ?? 0);
        if (actionRes) setActionRequired(actionRes.data ?? []);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : "등록 업체 목록을 불러오지 못했습니다.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchPage();
    return () => {
      cancelled = true;
    };
  }, [page]);

  // 무한 스크롤 — sentinel 이 화면에 보이면 다음 페이지 자동 요청.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  const filteredCompanies = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    return companies.filter((c) => {
      if (keyword) {
        const matched =
          c.companyName.toLowerCase().includes(keyword) ||
          c.ownerName.toLowerCase().includes(keyword) ||
          (c.businessNumber ?? "").includes(keyword);
        if (!matched) return false;
      }
      if (
        classificationFilter &&
        !(c.programCodes ?? []).includes(classificationFilter)
      )
        return false;
      if (collectorFilter && c.collectorStatus !== collectorFilter) return false;
      if (kpiReportFilter && c.kpiReportStatus !== kpiReportFilter) return false;
      return true;
    });
  }, [
    companies,
    searchKeyword,
    classificationFilter,
    collectorFilter,
    kpiReportFilter,
  ]);

  const handleRowClick = (companyId: number): void => {
    navigate(`/admin/company/manage/${companyId}`);
  };

  const handleReset = (): void => {
    setSearchKeyword("");
    setClassificationFilter("");
    setCollectorFilter("");
    setKpiReportFilter("");
  };

  return (
    <Pod24AdminLayout>
      <div
        className="text-[#252525]"
        style={{
          fontFamily:
            "Pretendard, -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        {/* 페이지 헤더 */}
        <div className="mb-6">
          <h1 className="text-[22px] font-bold text-[#222]">등록 업체 관리</h1>
          <p className="text-[13px] text-[#888] mt-1">
            총 {totalElements}개의 업체가 있습니다. 장비 보고 상태를 확인하고
            조치를 진행합니다.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg border border-[#4285F4]/20 bg-[#EBF3FF] text-[13px] text-[#4285F4]">
            {error}
          </div>
        )}

        {/* 조치 필요 업체 게시판 */}
        <section className="bg-white rounded-[10px] border border-[#D9D9D9] overflow-hidden mb-4">
          <div className="px-4 py-3 border-b border-[#F2C5C5] flex items-center justify-between bg-[#FFFAFA]">
            <div className="flex items-center gap-2.5 text-[13px]">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#F24A4A"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
                />
              </svg>
              <span className="font-semibold">조치 필요 업체</span>
              <span className="text-[#F24A4A] font-medium">
                {actionRequired.length}건
              </span>
              <span className="text-[#888] font-normal text-[12px] ml-2">
                임박도가 높은 순으로 자동 정렬됩니다.
              </span>
            </div>
            <div className="flex items-center gap-2 text-[12px] text-[#555]">
              <span>정렬:</span>
              <select className="border border-[#D9D9D9] rounded-lg text-[12px] py-1 px-2 outline-none bg-white">
                <option>임박도 ↓</option>
                <option>최근 보고일시 ↓</option>
                <option>업체명 가나다 ↑</option>
              </select>
              <button
                type="button"
                className="text-[12px] ml-2 text-[#555] hover:text-[#252525]"
              >
                전체 보기 ›
              </button>
            </div>
          </div>

          {/* 게시판 헤더 */}
          <div
            className="px-4 py-2 bg-[#FAFAFA] border-b border-[#D9D9D9] text-[11.5px] text-[#888] font-medium"
            style={{
              display: "grid",
              gridTemplateColumns:
                "220px 96px 96px minmax(210px, 1fr) minmax(210px, 1fr) 150px 32px",
              gap: 8,
            }}
          >
            <div>회사이름</div>
            <div className="text-center">대표자명</div>
            <div className="text-center">업체유형</div>
            <div className="text-center">데이터수집기 상태</div>
            <div className="text-center">전일 KPI 보고 상태</div>
            <div className="text-center">최근 보고</div>
            <div />
          </div>

          {actionRequired.length === 0 && !loading && (
            <div className="px-4 py-6 text-[13px] text-[#888] text-center">
              조치가 필요한 업체가 없습니다.
            </div>
          )}

          {actionRequired.map((row) => (
            <div
              key={row.companyId}
              role="button"
              onClick={() => handleRowClick(row.companyId)}
              className="px-4 py-3 border-b border-[#EEE] last:border-b-0 cursor-pointer hover:bg-[#FAFAFA] items-center text-[13px] relative"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "220px 96px 96px minmax(210px, 1fr) minmax(210px, 1fr) 150px 32px",
                gap: 8,
              }}
            >
              <div
                className="absolute left-0 top-0 bottom-0"
                style={{
                  width: 3,
                  background: row.urgency
                    ? URGENCY_BAR[row.urgency]
                    : "transparent",
                }}
              />
              <div className="flex items-center gap-2.5">
                <LogoMini code={buildLogoCode(row.companyName)} />
                <div className="font-medium">{row.companyName}</div>
                <NoticeBadge row={row} />
              </div>
              <div className="text-[#555] text-center">{row.ownerName}</div>
              <div className="text-[#555] text-center">
                {formatClassification(row)}
              </div>
              <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: COLLECTOR_DOT[row.collectorStatus] }}
                />
                <span>{COLLECTOR_LABEL[row.collectorStatus]}</span>
                {row.collectorNote && (
                  <span className="text-[11px] text-[#888]">
                    · {row.collectorNote}
                  </span>
                )}
              </div>
              <div className="flex justify-center">
                <KpiReportCell
                  status={row.kpiReportStatus}
                  note={row.kpiReportNote}
                />
              </div>
              <div className="text-[#888] text-[12px] text-center">
                {formatLastReportedAt(row.lastReportedAt)}
              </div>
              <div className="text-[#888] text-right">›</div>
            </div>
          ))}
        </section>

        {/* 필터 박스 */}
        <div className="mb-4 p-5 border border-[#D9D9D9] rounded-[10px] bg-white">
          <div className="flex flex-col md:flex-row gap-3 mb-3">
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="flex-1 border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4]"
              placeholder="🔍 회사명, 대표자명, 사업자번호로 검색"
            />
            <button
              onClick={handleReset}
              className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] min-w-[85px]"
            >
              초기화
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11.5px] text-[#555] block mb-1">
                업체 분류
              </label>
              <select
                value={classificationFilter}
                onChange={(e) =>
                  setClassificationFilter(e.target.value as "" | ProgramCode)
                }
                className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4]"
              >
                <option value="">전체 등록 업체</option>
                <option value="SMART_WORKSHOP">스마트 공방</option>
                <option value="SMART_FACTORY">스마트 공장</option>
              </select>
            </div>
            <div>
              <label className="text-[11.5px] text-[#555] block mb-1">
                데이터 수집기 상태
              </label>
              <select
                value={collectorFilter}
                onChange={(e) =>
                  setCollectorFilter(e.target.value as "" | CollectorStatus)
                }
                title="정상 · 경고: 최근 1일 이상 데이터 수집이 안된 업체 · 오프라인: 수집기의 마지막 health check 가 10분 이상 넘어간 경우"
                className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4]"
              >
                <option value="">전체 수집기 상태</option>
                <option value="NORMAL">정상</option>
                <option value="WARNING">경고 (1일 이상 미수집)</option>
                <option value="OFFLINE">오프라인 (health check 10분 초과)</option>
                <option value="NOT_INSTALLED">미설치</option>
              </select>
            </div>
            <div>
              <label className="text-[11.5px] text-[#555] block mb-1">
                전일 KPI 보고 상태
              </label>
              <select
                value={kpiReportFilter}
                onChange={(e) =>
                  setKpiReportFilter(
                    e.target.value as "" | KpiDailyReportStatus,
                  )
                }
                title="정상: KPI 가 정상적으로 보고됨 · 미보고: 전일 장비가 가동되지 않은 경우 · 오류: 장비는 가동되었으나 오류가 발생한 경우"
                className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4]"
              >
                <option value="">전체 전일 KPI 보고</option>
                <option value="NORMAL">정상</option>
                <option value="NOT_REPORTED">미보고 (전일 미가동)</option>
                <option value="ERROR">오류 (가동 중 오류)</option>
                <option value="PENDING">승인 대기</option>
              </select>
            </div>
          </div>
        </div>

        {/* 전체 테이블 */}
        <div className="rounded-lg overflow-x-auto border border-[#D9D9D9] bg-white mb-4">
          <table
            className="w-full"
            style={{ borderCollapse: "separate", borderSpacing: 0 }}
          >
            <thead>
              <tr>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-4 text-left border-b border-[#D9D9D9] w-[240px]">
                  회사이름
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-4 text-center border-b border-[#D9D9D9] w-[200px]">
                  대표자명
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-4 text-center border-b border-[#D9D9D9] w-[100px]">
                  업체유형
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-4 text-center border-b border-[#D9D9D9]">
                  데이터수집기 상태
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-4 text-center border-b border-[#D9D9D9] w-[180px]">
                  전일 KPI 보고 상태
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-4 text-center border-b border-[#D9D9D9] w-[180px]">
                  최근 보고
                </th>
                <th className="bg-[#F8F8F8] py-3 px-4 border-b border-[#D9D9D9] w-[40px]" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-8 px-4 text-[13px] text-center text-[#888]"
                  >
                    불러오는 중...
                  </td>
                </tr>
              )}
              {!loading &&
                filteredCompanies.map((row) => (
                  <tr
                    key={row.companyId}
                    onClick={() => handleRowClick(row.companyId)}
                    className="hover:bg-[#F2F2F2] cursor-pointer"
                  >
                    <td className="py-3 px-4 text-[13px] border-b border-[#D9D9D9] last:border-b-0">
                      <div className="flex items-center gap-3">
                        <LogoMini code={buildLogoCode(row.companyName)} />
                        <div className="font-medium">{row.companyName}</div>
                        <NoticeBadge row={row} />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[13px] text-[#555] text-center border-b border-[#D9D9D9]">
                      {row.ownerName}
                    </td>
                    <td className="py-3 px-4 text-[13px] text-[#555] text-center border-b border-[#D9D9D9]">
                      {formatClassification(row)}
                    </td>
                    <td className="py-3 px-4 text-[13px] border-b border-[#D9D9D9]">
                      <div className="flex justify-center">
                        {formatCollectorSummary(row)}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[13px] border-b border-[#D9D9D9]">
                      <div className="flex justify-center">
                        <KpiReportCell
                          status={row.kpiReportStatus}
                          note={row.kpiReportNote}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[13px] text-center whitespace-nowrap border-b border-[#D9D9D9]">
                      {row.lastReportedAt ? (
                        <span className="text-[#555]">
                          {formatLastReportedAt(row.lastReportedAt)}
                        </span>
                      ) : (
                        <span className="text-[#888]">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-[13px] text-[#888] text-right border-b border-[#D9D9D9]">
                      ›
                    </td>
                  </tr>
                ))}
              {!loading && filteredCompanies.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-8 px-4 text-[13px] text-center text-[#888]"
                  >
                    검색 조건에 해당하는 업체가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 (UI 만 — 기능은 추후 연결) */}
        {/* 무한 스크롤 — sentinel 이 보이면 자동으로 다음 페이지 로드 */}
        {hasMore && (
          <div
            ref={sentinelRef}
            className="text-center text-[12px] text-[#888] py-4 mb-4"
          >
            {loading ? "불러오는 중..." : "스크롤하면 더 불러옵니다"}
          </div>
        )}
        {!hasMore && companies.length > 0 && (
          <div className="text-center text-[12px] text-[#888] py-3 mb-4">
            전체 {totalElements} 개 업체를 모두 불러왔습니다
          </div>
        )}
      </div>
    </Pod24AdminLayout>
  );
};

export default ManageRegisteredCompanies1;